import { create } from 'zustand'

import { ToastData, ToastType } from '@/components/ui/toast'
import { generateId } from '@/utils'

import {
  DEFAULT_DURATION,
  EXIT_ANIMATION_DURATION,
  MAX_VISIBLE_TOASTS,
} from './create-toast-factory.constants'
import { ShowToastOptions, ToastMethods, ToastStoreState } from './create-toast-factory.types'
import { ToastProvider } from './toast-provider'

export const createToastFactory = () => {
  const useToastStore = create<ToastStoreState>(() => ({
    mountedProviders: 0,
    toasts: [],
  }))

  // Note: numbers every show() - repeats within the same millisecond still restart the timer
  let showCount = 0

  const mountProvider = () => {
    useToastStore.setState((state) => {
      if (state.mountedProviders > 0) {
        console.warn(
          `You are trying to mount multiple 'ToastProviders' from 'createToastFactory'.\nThis will lead to problem that you will see multiple identical copies of toasts.\nPlease review your code and make sure you are only mounting one 'ToastProvider'`,
        )
      }

      return { ...state, mountedProviders: state.mountedProviders + 1 }
    })

    return () => {
      useToastStore.setState((state) => ({
        ...state,
        mountedProviders: state.mountedProviders - 1,
      }))
    }
  }

  const checkForProvider = () => {
    const hasProvider = useToastStore.getState().mountedProviders > 0
    if (!hasProvider) {
      console.warn(
        `You are trying to operate with a 'createToastFactory' without mounting the 'ToastProvider' from it.\nPlease make sure you are mounting the 'ToastProvider' before using 'createToastFactory'`,
      )
    }
    return hasProvider
  }

  const removeToast = (id: string) => {
    useToastStore.setState((state) => ({
      ...state,
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }))
  }

  const markAsExiting = (id: string) => {
    useToastStore.setState((state) => ({
      ...state,
      toasts: state.toasts.map((toast) =>
        toast.id === id ? { ...toast, isExiting: true } : toast,
      ),
    }))
  }

  const dismiss = (id: string) => {
    const toast = useToastStore.getState().toasts.find((t) => t.id === id)
    if (!toast || toast.isExiting) return

    markAsExiting(id)
    setTimeout(() => removeToast(id), EXIT_ANIMATION_DURATION)
  }

  const show = (
    message: string,
    type: ToastType = 'success',
    options: ShowToastOptions = {},
  ): string => {
    if (!checkForProvider()) return ''

    const { duration = DEFAULT_DURATION, description, onRepeat = 'restart' } = options
    const key = options.key ?? [type, message, description].join('|')
    showCount += 1
    const shownVersion = showCount

    // Note: the same toast already on screen (not on its way out) is never repeated - it's either
    // restarted in place, or closed so the new one below takes its place
    const existing = useToastStore
      .getState()
      .toasts.find((toast) => toast.key === key && !toast.isExiting)
    if (existing && onRepeat === 'replace') {
      dismiss(existing.id)
    } else if (existing) {
      useToastStore.setState((state) => ({
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === existing.id
            ? { ...toast, description, duration, message, shownVersion }
            : toast,
        ),
      }))
      return existing.id
    }

    const id = generateId('toast')
    const newToast: ToastData = {
      description,
      duration,
      id,
      key,
      message,
      shownVersion,
      type,
    }

    useToastStore.setState((state) => ({
      ...state,
      toasts: [...state.toasts, newToast],
    }))

    // Note: over the limit, the oldest toasts leave (with their exit animation) to make room
    const visible = useToastStore.getState().toasts.filter((toast) => !toast.isExiting)
    visible.slice(0, Math.max(visible.length - MAX_VISIBLE_TOASTS, 0)).forEach((toast) => {
      dismiss(toast.id)
    })

    return id
  }

  const success = (message: string, options: ShowToastOptions = {}): string => {
    return show(message, 'success', options)
  }

  const error = (message: string, options: ShowToastOptions = {}): string => {
    return show(message, 'error', options)
  }

  const methods: ToastMethods = {
    error,
    success,
  }

  return {
    ...methods,
    ToastProvider: () => (
      <ToastProvider
        useToastStore={useToastStore}
        mountProvider={mountProvider}
        dismiss={dismiss}
      />
    ),
    useStore: useToastStore,
  }
}
